/* eslint-disable react/require-default-props */
import { brandfolderColorPalette } from '@brandfolder/react';
import { t } from '@lingui/macro';
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';

interface SectionOption {
  key: string;
  name: string;
}

interface SectionsFilterProps {
  filters: {
    sections?: SectionOption[];
  };
  sections: Array<{ section_key: string; name: string }>;
  updateFilters: (key: string, name: string) => void;
}

const Wrapper = styled.div`
  padding: 20px 25px;
  border-bottom: 1px solid ${brandfolderColorPalette.neutrals[300]};
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${brandfolderColorPalette.neutrals[700]};
  margin-bottom: 8px;
`;

const InputContainer = styled.div<{ $isOpen: boolean }>`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-height: 48px;
  padding: 6px 36px 6px 8px;
  border: 1px solid ${({ $isOpen }): string =>
    $isOpen
      ? brandfolderColorPalette.primary[600]
      : brandfolderColorPalette.neutrals[300]};
  border-radius: 4px;
  background: ${brandfolderColorPalette.neutrals[0]};
  cursor: text;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ $isOpen }): string =>
    $isOpen
      ? brandfolderColorPalette.primary[600]
      : brandfolderColorPalette.neutrals[400]};
  }
`;

const AccordionArrow = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  right: 20px;
  top: 50%;
  width: 8px;
  height: 8px;
  border-right: 2px solid ${brandfolderColorPalette.neutrals[700]};
  border-bottom: 2px solid ${brandfolderColorPalette.neutrals[700]};
  transform: ${({ $isOpen }): string =>
    $isOpen
      ? 'translateY(-30%) rotate(-135deg)'
      : 'translateY(-70%) rotate(45deg)'};
  transition: transform 0.2s ease;
  pointer-events: none;
  z-index: 1;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 80px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  color: ${brandfolderColorPalette.neutrals[700]};
  padding: 4px 0;

  &::placeholder {
    color: ${brandfolderColorPalette.neutrals[400]};
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${brandfolderColorPalette.primary[100]};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${brandfolderColorPalette.neutrals[700]};
  max-width: 180px;
`;

const ChipName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${brandfolderColorPalette.neutrals[500]};
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: ${brandfolderColorPalette.neutrals[700]};
  }
`;

const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 2;
  margin: 4px 0 0;
  padding: 4px 0;
  list-style: none;
  background: ${brandfolderColorPalette.neutrals[0]};
  border-radius: 4px;
  box-shadow: 0 0 20px 0 hsla(0, 0%, 86.7%, 0.55);
  max-height: 240px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${brandfolderColorPalette.neutrals[100]};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${brandfolderColorPalette.neutrals[300]};
    border-radius: 4px;
    &:hover {
      background: ${brandfolderColorPalette.neutrals[400]};
    }
  }
  scrollbar-width: thin;
  scrollbar-color: ${brandfolderColorPalette.neutrals[300]} ${brandfolderColorPalette.neutrals[100]};
`;

const DropdownItem = styled.li<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  background: ${({ $isSelected }): string =>
    $isSelected ? brandfolderColorPalette.primary[50] : 'transparent'};

  &:hover {
    background: ${brandfolderColorPalette.neutrals[100]};
  }
`;

const Checkbox = styled.span<{ $checked: boolean }>`
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid
    ${({ $checked }): string =>
    $checked
      ? brandfolderColorPalette.primary[600]
      : brandfolderColorPalette.neutrals[300]};
  border-radius: 4px;
  background: ${({ $checked }): string =>
    $checked ? brandfolderColorPalette.primary[600] : brandfolderColorPalette.neutrals[0]};
  flex-shrink: 0;
  position: relative;

  &::after {
    content: ${({ $checked }: { $checked: boolean }): string => ($checked ? "''" : 'none')};
    position: absolute;
    left: 5px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const OptionName = styled.span`
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const EmptyMessage = styled.li`
  padding: 12px 16px;
  font-size: 14px;
  color: ${brandfolderColorPalette.neutrals[400]};
  text-align: center;
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const AccordionButton = styled.button`
  position: absolute;
  right: 5px;
  top: 5px;
  bottom: 5px;
  width: 40px;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s ease;
  z-index: 0;

  &:hover {
    background: ${brandfolderColorPalette.neutrals[100]};
  }
`;

const SectionsFilter: React.FunctionComponent<SectionsFilterProps> = ({
  filters,
  sections,
  updateFilters,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedSections = filters.sections || [];
  const selectedKeys = new Set(selectedSections.map((s) => s.key));

  const availableSections = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const options = sections.map((s) => ({ key: s.section_key, name: s.name }));
    if (!query) return options;
    return options.filter((s) => s.name.toLowerCase().includes(query));
  }, [sections, searchValue]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (!isOpen) setIsOpen(true);
  }, [isOpen]);

  const handleContainerClick = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleAccordionToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpen) {
      setIsOpen(false);
      setSearchValue('');
      inputRef.current?.blur();
    } else {
      setIsOpen(true);
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleOptionClick = useCallback((option: SectionOption) => {
    updateFilters(option.key, option.name);
    setSearchValue('');
    inputRef.current?.focus();
  }, [updateFilters]);

  const handleChipRemove = useCallback((section: SectionOption) => {
    updateFilters(section.key, section.name);
  }, [updateFilters]);

  const placeholder = selectedSections.length === 0 ? t`Choose section` : '';

  return (
    <Wrapper>
      <Label>{t`Section`}</Label>
      <DropdownWrapper ref={wrapperRef}>
        <InputContainer $isOpen={isOpen} onClick={handleContainerClick}>
          {selectedSections.map((section) => (
            <Chip key={section.key}>
              <ChipName title={section.name}>{section.name}</ChipName>
              <ChipClose
                aria-label={t`Remove ${section.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChipRemove(section);
                }}
                type="button"
              >
                ×
              </ChipClose>
            </Chip>
          ))}
          <SearchInput
            ref={inputRef}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            type="text"
            value={searchValue}
          />
        </InputContainer>
        <AccordionButton
          aria-label={isOpen ? t`Close dropdown` : t`Open dropdown`}
          onClick={handleAccordionToggle}
          type="button"
        />
        <AccordionArrow $isOpen={isOpen} />

        {isOpen && (
          <DropdownList aria-label={t`List of sections`} role="listbox">
            {availableSections.length === 0 && (
              <EmptyMessage>{t`No sections found`}</EmptyMessage>
            )}
            {availableSections.map((option) => {
              const isSelected = selectedKeys.has(option.key);
              return (
                <DropdownItem
                  key={option.key}
                  $isSelected={isSelected}
                  aria-selected={isSelected}
                  onClick={() => handleOptionClick(option)}
                  role="option"
                >
                  <Checkbox $checked={isSelected} />
                  <OptionName title={option.name}>{option.name}</OptionName>
                </DropdownItem>
              );
            })}
          </DropdownList>
        )}
      </DropdownWrapper>
    </Wrapper>
  );
};

export default SectionsFilter;
